import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { MAX_APPS } from "~/constants/appGenerationLimits";
import { normalizeMarkdownContent } from "~/features/projectTop/pageView/utils/markdownParser";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import type { RootState } from "~/store";

export interface AppData {
  appid: number;
  appname: string;
  description: string;
}

export interface ConnectorData {
  connectorid: number;
  sid: number;
  tid: number;
  relationship: string;
  keyfield: string;
  referencefield: string;
}

export interface RequirementData {
  appid: number;
  rid: string;
  name: string;
  description: string;
  how: string;
}

export interface AppDesignState {
  appData: AppData[];
  connectorData: ConnectorData[];
  requirementData: RequirementData[];
  points: string[];
  copyAppData: AppData[];
  copyConnectorData: ConnectorData[];
  copyRequirementData: RequirementData[];
  copyPoints: string[];
  isEditable: boolean;
  isLoading: boolean;
  error: string | null;
  isMarkdown: boolean;
  markdownContent: string;
  rawContent: string;
  initialMarkdownContent: string;
  sources: { type: string; id: string }[];
  documentInfo: {
    createdAt: string;
    modifiedAt: string;
    modifiedBy?: {
      display_name: string;
    };
  } | null;
}

const initialState: AppDesignState = {
  appData: [],
  connectorData: [],
  requirementData: [],
  points: [],
  copyAppData: [],
  copyConnectorData: [],
  copyRequirementData: [],
  copyPoints: [],
  isEditable: false,
  isLoading: true,
  error: null,
  isMarkdown: false,
  markdownContent: "",
  rawContent: "",
  initialMarkdownContent: "",
  sources: [],
  documentInfo: null,
};

const documentApi = new DocumentApi();
const processLockApi = new ProcessLockApi();

export const fetchAppDesign = createAsyncThunk(
  "appDesign/fetch",
  async (documentIdArg: string | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      // URLパラメータから取得したIDを優先、なければReduxストアから取得
      const documentId = documentIdArg || currentProject.outputs?.["app-design"]?.id;

      if (!documentId) {
        return rejectWithValue("アプリ設計書が見つかりません");
      }
      const response = await documentApi.get(
        currentUser.organization_id,
        currentProject.id,
        documentId,
      );
      if (response === null) {
        return rejectWithValue("データの取得に失敗しました");
      }

      const rawContent = response.content ?? "";
      let appData: AppData[] = [];
      let connectorData: ConnectorData[] = [];
      let requirementData: RequirementData[] = [];
      let points: string[] = [];
      let isMarkdown = false;
      let markdownContent = "";

      try {
        const parsed = JSON.parse(rawContent);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          const parsedRecord = parsed as Record<string, unknown>;

          // フラット構造（英語キー）をチェック: app_table, connector_table, function_table
          const flatAppTable = parsedRecord["app_table"] as { rows?: unknown[] } | undefined;
          const flatConnectorTable = parsedRecord["connector_table"] as
            | { rows?: unknown[] }
            | undefined;
          const flatFunctionTable = parsedRecord["function_table"] as
            | { rows?: unknown[] }
            | undefined;

          const hasFlatTables =
            Array.isArray(flatAppTable?.rows) &&
            Array.isArray(flatConnectorTable?.rows) &&
            Array.isArray(flatFunctionTable?.rows);

          if (hasFlatTables) {
            // フラット構造からデータを取得
            appData = flatAppTable!.rows!.map((item: any) => ({
              appid: Number(item?.[0] ?? 0),
              appname: item?.[1] ?? "",
              description: item?.[2] ?? "",
            }));
            connectorData = flatConnectorTable!.rows!.map((item: any) => ({
              connectorid: Number(item?.[0] ?? 0),
              sid: Number(item?.[1] ?? 0),
              tid: Number(item?.[2] ?? 0),
              relationship: item?.[3] ?? "",
              keyfield: item?.[4] ?? "",
              referencefield: item?.[5] ?? "",
            }));
            requirementData = flatFunctionTable!.rows!.map((item: any) => ({
              appid: Number(item?.[0] ?? 0),
              rid: String(item?.[1] ?? ""),
              name: item?.[2] ?? "",
              description: item?.[3] ?? "",
              how: item?.[4] ?? "",
            }));
            points = Array.isArray(parsedRecord["considerations"])
              ? (parsedRecord["considerations"] as string[])
              : Array.isArray(parsedRecord["考慮点"])
                ? (parsedRecord["考慮点"] as string[])
                : [];
          } else {
            // ネスト構造をチェック: tables または テーブル一覧
            const tableContainer = parsedRecord["テーブル一覧"] ?? parsedRecord["tables"];
            const hasValidTables = tableContainer && typeof tableContainer === "object";

            if (hasValidTables) {
              const tables = tableContainer as Record<string, { rows?: unknown[] }>;
              const appTableRows = Array.isArray(tables["アプリテーブル"]?.rows)
                ? tables["アプリテーブル"]!.rows!
                : null;
              const connectorTableRows = Array.isArray(tables["アプリ間連携テーブル"]?.rows)
                ? tables["アプリ間連携テーブル"]!.rows!
                : null;
              const requirementTableRows = Array.isArray(tables["要求機能テーブル"]?.rows)
                ? tables["要求機能テーブル"]!.rows!
                : null;

              if (appTableRows && connectorTableRows && requirementTableRows) {
                appData = appTableRows.map((item: any) => ({
                  appid: Number(item?.[0] ?? 0),
                  appname: item?.[1] ?? "",
                  description: item?.[2] ?? "",
                }));
                connectorData = connectorTableRows.map((item: any) => ({
                  connectorid: Number(item?.[0] ?? 0),
                  sid: Number(item?.[1] ?? 0),
                  tid: Number(item?.[2] ?? 0),
                  relationship: item?.[3] ?? "",
                  keyfield: item?.[4] ?? "",
                  referencefield: item?.[5] ?? "",
                }));
                requirementData = requirementTableRows.map((item: any) => ({
                  appid: Number(item?.[0] ?? 0),
                  rid: String(item?.[1] ?? ""),
                  name: item?.[2] ?? "",
                  description: item?.[3] ?? "",
                  how: item?.[4] ?? "",
                }));
                points = Array.isArray(parsedRecord["考慮点"])
                  ? (parsedRecord["考慮点"] as string[])
                  : Array.isArray(parsedRecord["considerations"])
                    ? (parsedRecord["considerations"] as string[])
                    : [];
              } else {
                isMarkdown = true;
                markdownContent = normalizeMarkdownContent(rawContent);
              }
            } else {
              isMarkdown = true;
              markdownContent = normalizeMarkdownContent(rawContent);
            }
          }
        } else {
          isMarkdown = true;
          markdownContent = normalizeMarkdownContent(rawContent);
        }
      } catch (_error) {
        isMarkdown = true;
        markdownContent = normalizeMarkdownContent(rawContent);
      }

      return {
        appData,
        connectorData,
        requirementData,
        points,
        isMarkdown,
        markdownContent,
        rawContent,
        documentInfo: {
          createdAt: response.created_at || "",
          modifiedAt: response.modified_at || "",
          modifiedBy: response.modified_by,
        },
        sources: response.sources ?? [],
      };
    } catch (_error) {
      return rejectWithValue("データの取得中にエラーが発生しました");
    }
  },
);

export const acquireAppDesignLock = createAsyncThunk(
  "appDesign/acquireLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;
      if (!currentProject.outputs?.["app-design"]?.id) {
        return rejectWithValue("アプリ設計書が見つかりません");
      }
      const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
        type: "app-design",
        mode: "edit",
      });
      if (response === null) {
        return rejectWithValue("編集権限を取得できませんでした");
      }
      return true;
    } catch (_error) {
      return rejectWithValue("ロックの取得中にエラーが発生しました");
    }
  },
);

export const releaseAppDesignLock = createAsyncThunk(
  "appDesign/releaseLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;
      if (!currentProject.outputs?.["app-design"]?.id) {
        return rejectWithValue("アプリ設計書が見つかりません");
      }
      await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
        type: "app-design",
        mode: "edit",
      });
      return true;
    } catch (_error) {
      return rejectWithValue("ロックの解放中にエラーが発生しました");
    }
  },
);

export const updateAppDesign = createAsyncThunk(
  "appDesign/update",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;
      const { appData, connectorData, requirementData, points, isMarkdown, markdownContent } =
        state.appDesign;
      if (!currentProject.outputs?.["app-design"]?.id) {
        return rejectWithValue("アプリ設計書が見つかりません");
      }

      if (isMarkdown) {
        const updatePayload = {
          content: markdownContent,
        };
        const updateContentResponse = await documentApi.updateContent(
          currentUser.organization_id,
          currentProject.id,
          currentProject.outputs?.["app-design"]?.id,
          updatePayload,
        );

        if (updateContentResponse === null) {
          return rejectWithValue("保存に失敗しました");
        }

        await dispatch(releaseAppDesignLock());
        return true;
      }

      const hasEmptyApp = appData.some(
        (app) =>
          app.appid === 0 ||
          String(app.appid).trim() === "" ||
          app.appname.trim() === "" ||
          app.description.trim() === "",
      );

      if (hasEmptyApp) {
        return rejectWithValue(
          "作成するアプリに空の項目があります。すべての項目（アプリID、アプリ名、説明）を入力してから保存してください。",
        );
      }

      const hasEmptyConnector = connectorData.some(
        (conn) =>
          conn.connectorid === 0 ||
          conn.sid === 0 ||
          conn.tid === 0 ||
          String(conn.connectorid).trim() === "" ||
          String(conn.sid).trim() === "" ||
          String(conn.tid).trim() === "" ||
          conn.relationship.trim() === "" ||
          conn.keyfield.trim() === "" ||
          conn.referencefield.trim() === "",
      );

      if (hasEmptyConnector) {
        return rejectWithValue(
          "アプリ間の連携に空の項目があります。すべての項目（連携ID、送信元ID、送信先ID、連携方法、キーフィールド、参照フィールド）を入力してから保存してください。",
        );
      }

      const hasEmptyRequirement = requirementData.some(
        (req) =>
          req.appid === 0 ||
          req.rid.trim() === "" ||
          String(req.appid).trim() === "" ||
          req.name.trim() === "" ||
          req.description.trim() === "" ||
          req.how.trim() === "",
      );

      if (hasEmptyRequirement) {
        return rejectWithValue(
          "アプリ毎の機能に空の項目があります。すべての項目（アプリID、機能ID、機能、説明、実装方法）を入力してから保存してください。",
        );
      }

      const appTableHeaders = ["app_id", "app_name", "description"];
      const connectorTableHeaders = [
        "connector_id",
        "sid",
        "tid",
        "relationship",
        "keyfield",
        "referencefield",
      ];
      const requirementTableHeaders = ["app_id", "rid", "name", "description", "how"];
      const updatedContent = {
        テーブル一覧: {
          アプリテーブル: {
            headers: appTableHeaders,
            rows: appData.map((item) => [item.appid.toString(), item.appname, item.description]),
            description: "",
          },
          アプリ間連携テーブル: {
            headers: connectorTableHeaders,
            rows: connectorData.map((item) => [
              item.connectorid.toString(),
              item.sid.toString(),
              item.tid.toString(),
              item.relationship,
              item.keyfield,
              item.referencefield,
            ]),
            description: "",
          },
          要求機能テーブル: {
            headers: requirementTableHeaders,
            rows: requirementData.map((item) => [
              item.appid.toString(),
              item.rid.toString(),
              item.name,
              item.description,
              item.how,
            ]),
            description: "",
          },
        },
        考慮点: points,
      };
      const updatePayload = {
        content: JSON.stringify(updatedContent),
      };
      const updateContentResponse = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        currentProject.outputs?.["app-design"]?.id,
        updatePayload,
      );

      await dispatch(releaseAppDesignLock());
      if (updateContentResponse !== null) {
        return true;
      } else {
        return rejectWithValue("保存に失敗しました");
      }
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

export const saveAppDesignMarkdownContent = createAsyncThunk(
  "appDesign/saveMarkdownContent",
  async (content: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;
      const documentId = currentProject.outputs?.["app-design"]?.id;

      if (!documentId) {
        return rejectWithValue("アプリ設計書が見つかりません");
      }

      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        documentId,
        { content },
      );

      if (response === null) {
        return rejectWithValue("データの更新に失敗しました");
      }

      return true;
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

const appDesignSlice = createSlice({
  name: "appDesign",
  initialState,
  reducers: {
    setAppData: (state, action: PayloadAction<AppData[]>) => {
      state.appData = action.payload;
      state.isMarkdown = false;
      state.markdownContent = "";
      state.initialMarkdownContent = "";
    },
    setConnectorData: (state, action: PayloadAction<ConnectorData[]>) => {
      state.connectorData = action.payload;
    },
    setRequirementData: (state, action: PayloadAction<RequirementData[]>) => {
      state.requirementData = action.payload;
    },
    setPoints: (state, action: PayloadAction<string[]>) => {
      state.points = action.payload;
    },
    setCopyData: (state) => {
      state.copyAppData = JSON.parse(JSON.stringify(state.appData));
      state.copyConnectorData = JSON.parse(JSON.stringify(state.connectorData));
      state.copyRequirementData = JSON.parse(JSON.stringify(state.requirementData));
      state.copyPoints = JSON.parse(JSON.stringify(state.points));
      state.initialMarkdownContent = state.markdownContent;
    },
    restoreFromCopyData: (state) => {
      state.appData = JSON.parse(JSON.stringify(state.copyAppData));
      state.connectorData = JSON.parse(JSON.stringify(state.copyConnectorData));
      state.requirementData = JSON.parse(JSON.stringify(state.copyRequirementData));
      state.points = JSON.parse(JSON.stringify(state.copyPoints));
      state.markdownContent = state.initialMarkdownContent;
    },
    updateAppRow: (state, action: PayloadAction<{ index: number; data: AppData }>) => {
      state.appData[action.payload.index] = action.payload.data;
    },
    updateConnectorRow: (state, action: PayloadAction<{ index: number; data: ConnectorData }>) => {
      state.connectorData[action.payload.index] = action.payload.data;
    },
    updateRequirementRow: (
      state,
      action: PayloadAction<{ index: number; data: RequirementData }>,
    ) => {
      state.requirementData[action.payload.index] = action.payload.data;
    },
    addAppRow: (state, action: PayloadAction<AppData>) => {
      if (state.appData.length >= MAX_APPS) {
        return;
      }
      state.appData.push(action.payload);
    },
    addConnectorRow: (state, action: PayloadAction<ConnectorData>) => {
      state.connectorData.push(action.payload);
    },
    addRequirementRow: (state, action: PayloadAction<RequirementData>) => {
      state.requirementData.push(action.payload);
    },
    deleteAppRow: (state, action: PayloadAction<number>) => {
      state.appData = state.appData.filter((_, idx) => idx !== action.payload);
    },
    deleteConnectorRow: (state, action: PayloadAction<number>) => {
      state.connectorData = state.connectorData.filter((_, idx) => idx !== action.payload);
    },
    deleteRequirementRow: (state, action: PayloadAction<number>) => {
      state.requirementData = state.requirementData.filter((_, idx) => idx !== action.payload);
    },
    addPoint: (state) => {
      state.points.push("");
    },
    updatePoint: (state, action: PayloadAction<{ index: number; value: string }>) => {
      state.points[action.payload.index] = action.payload.value;
    },
    deletePoint: (state, action: PayloadAction<number>) => {
      state.points = state.points.filter((_, idx) => idx !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    updateMarkdownContent: (state, action: PayloadAction<string>) => {
      state.markdownContent = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppDesign.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAppDesign.fulfilled, (state, action) => {
        state.isMarkdown = action.payload?.isMarkdown || false;
        state.markdownContent = action.payload?.markdownContent || "";
        state.rawContent = action.payload?.rawContent || "";
        state.initialMarkdownContent = state.markdownContent;
        state.sources = action.payload?.sources ?? [];
        if (state.isMarkdown) {
          state.appData = [];
          state.connectorData = [];
          state.requirementData = [];
          state.points = [];
          state.copyAppData = [];
          state.copyConnectorData = [];
          state.copyRequirementData = [];
          state.copyPoints = [];
        } else {
          state.appData = action.payload?.appData || [];
          state.connectorData = action.payload?.connectorData || [];
          state.requirementData = action.payload?.requirementData || [];
          state.points = action.payload?.points || [];
          state.copyAppData = JSON.parse(JSON.stringify(state.appData));
          state.copyConnectorData = JSON.parse(JSON.stringify(state.connectorData));
          state.copyRequirementData = JSON.parse(JSON.stringify(state.requirementData));
          state.copyPoints = JSON.parse(JSON.stringify(state.points));
        }
        if (action.payload?.documentInfo) {
          if (action.payload.documentInfo.modifiedBy) {
            state.documentInfo = {
              createdAt: action.payload.documentInfo.createdAt,
              modifiedAt: action.payload.documentInfo.modifiedAt,
              modifiedBy: {
                display_name: action.payload.documentInfo.modifiedBy.display_name,
              },
            };
          } else {
            state.documentInfo = {
              createdAt: action.payload.documentInfo.createdAt,
              modifiedAt: action.payload.documentInfo.modifiedAt,
            };
          }
        } else {
          state.documentInfo = null;
        }
        state.isLoading = false;
      })
      .addCase(fetchAppDesign.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(acquireAppDesignLock.fulfilled, (state) => {
        state.isEditable = true;
        state.initialMarkdownContent = state.markdownContent;
      })
      .addCase(acquireAppDesignLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(releaseAppDesignLock.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(releaseAppDesignLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateAppDesign.pending, (state) => {
        state.error = null;
      })
      .addCase(updateAppDesign.fulfilled, (state) => {
        state.isEditable = false;
        if (state.isMarkdown) {
          state.initialMarkdownContent = state.markdownContent;
        } else {
          state.copyAppData = JSON.parse(JSON.stringify(state.appData));
          state.copyConnectorData = JSON.parse(JSON.stringify(state.connectorData));
          state.copyRequirementData = JSON.parse(JSON.stringify(state.requirementData));
          state.copyPoints = JSON.parse(JSON.stringify(state.points));
        }
      })
      .addCase(updateAppDesign.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(saveAppDesignMarkdownContent.fulfilled, (_state) => {
        // isEditable は releaseLock.fulfilled で false になる
      })
      .addCase(saveAppDesignMarkdownContent.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setAppData,
  setConnectorData,
  setRequirementData,
  setPoints,
  setCopyData,
  restoreFromCopyData,
  updateAppRow,
  updateConnectorRow,
  updateRequirementRow,
  addAppRow,
  addConnectorRow,
  addRequirementRow,
  deleteAppRow,
  deleteConnectorRow,
  deleteRequirementRow,
  addPoint,
  updatePoint,
  deletePoint,
  clearError,
  updateMarkdownContent,
} = appDesignSlice.actions;

export default appDesignSlice.reducer;
